import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

// The selection-box sweep plays once on mount, then settles at full width.
// Everything below is derived from these so nothing can drift out of phase.
const DRAG_MS = 2200;
const TYPE_MS = 1600;
const HOLD_MS = 1800;
const DRAG_EASE = 'cubic-bezier(0.18, 0.92, 0.18, 1)';

const HeroSection = styled.section`
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
  padding: 140px 24px 140px;

  @media (max-width: 768px) {
    padding: 120px 20px 180px;
  }
`;

const Welcome = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: clamp(1.05rem, 1.5vw, 1.35rem);
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 16px;
`;

const flareSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const Flare = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.accent};
  animation: ${flareSpin} 8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StatementWrap = styled.div`
  position: relative;
  margin-top: 28px;
  width: 100%;
  max-width: min(1280px, calc(100vw - 48px));
  height: 142px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    max-width: calc(100vw - 28px);
    height: 100px;
    margin-top: 20px;
  }
`;

const StatementStage = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 18px 22px 34px;
  transform: translateZ(0);

  @media (max-width: 768px) {
    padding: 14px 12px 30px;
  }
`;

const typeReveal = keyframes`
  from { clip-path: inset(0 100% 0 0); }
  to { clip-path: inset(0 0 0 0); }
`;

const dragBox = keyframes`
  0% { width: 18%; transform: translateY(-50%) scaleY(0.97); }
  16% { width: 33%; transform: translateY(-50%) scaleY(1); }
  42% { width: 76%; transform: translateY(-50%) scaleY(1.035); }
  58% { width: 97%; transform: translateY(-50%) scaleY(1.075); }
  70% { width: 100%; transform: translateY(-50%) scaleY(1.025); }
  82% { width: 101.5%; transform: translateY(-50%) scaleY(1.045); }
  100% { width: 100%; transform: translateY(-50%) scaleY(1); }
`;

const handleNudge = keyframes`
  0% { transform: translate(var(--x), var(--y)) scale(0.94); }
  42% { transform: translate(calc(var(--x) * 1.25), calc(var(--y) * 1.12)) scale(1.08); }
  58% { transform: translate(calc(var(--x) * 1.45), calc(var(--y) * 1.22)) scale(0.94); }
  70% { transform: translate(calc(var(--x) * 1.12), calc(var(--y) * 1.08)) scale(1.04); }
  100% { transform: translate(var(--x), var(--y)) scale(1); }
`;

const labelTrack = keyframes`
  0% { left: 18%; transform: translate(-50%, 0) scale(0.96); opacity: 0.9; }
  16% { left: 33%; transform: translate(-50%, 2px) scale(0.98); }
  42% { left: 76%; transform: translate(-50%, 7px) scale(1.01); opacity: 1; }
  58% { left: 97%; transform: translate(-50%, 10px) scale(1.035); }
  70% { left: 100%; transform: translate(-50%, 4px) scale(1); }
  82% { left: 101.5%; transform: translate(-50%, 7px) scale(1.015); }
  100% { left: 100%; transform: translate(-50%, 0) scale(0.99); }
`;

const cursorDrag = keyframes`
  0% { left: 18%; opacity: 0; transform: translate(-5px, 13px) rotate(-10deg) scale(0.95); }
  8% { left: 24%; opacity: 1; transform: translate(-4px, 14px) rotate(-11deg) scale(0.98); }
  42% { left: 76%; opacity: 1; transform: translate(3px, 22px) rotate(-13deg) scale(1.04); }
  58% { left: 97%; opacity: 1; transform: translate(9px, 29px) rotate(-11deg) scale(1.08); }
  70% { left: 100%; opacity: 1; transform: translate(4px, 20px) rotate(-12deg) scale(1); }
  82% { left: 101.5%; opacity: 1; transform: translate(8px, 24px) rotate(-11deg) scale(1.03); }
  100% { left: 100%; opacity: 0; transform: translate(2px, 17px) rotate(-12deg) scale(0.99); }
`;

const resumeButtonPulse = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
    box-shadow:
      0 24px 62px rgba(10, 10, 17, 0.11),
      0 0 0 1px rgba(24, 160, 251, 0.95);
  }
  44% {
    transform: translateY(-2px) scale(1.018);
    box-shadow:
      0 30px 72px rgba(10, 10, 17, 0.14),
      0 0 0 2px rgba(24, 160, 251, 1),
      0 0 0 10px rgba(24, 160, 251, 0.06);
  }
  62% {
    transform: translateY(0) scale(0.992);
    box-shadow:
      0 20px 54px rgba(10, 10, 17, 0.1),
      0 0 0 1px rgba(24, 160, 251, 0.95);
  }
`;

const ToolPlane = styled.div`
  --tool-blue: #18a0fb;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 22px;
  width: 100%;
  transform: none;
  z-index: 1;
  pointer-events: none;

  @media (max-width: 768px) {
    bottom: 20px;
  }
`;

const ResizeOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  width: 18%;
  height: 100%;
  border: 1px solid var(--tool-blue);
  background: linear-gradient(90deg, rgba(24, 160, 251, 0.1), rgba(24, 160, 251, 0.035));
  transform-origin: left center;
  animation: ${dragBox} ${DRAG_MS}ms ${DRAG_EASE} forwards;
  filter: drop-shadow(0 10px 22px rgba(24, 160, 251, 0.08));

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    width: 100%;
    transform: translateY(-50%);
  }
`;

const Handle = styled.span`
  --x: 0px;
  --y: 0px;
  position: absolute;
  width: 9px;
  height: 9px;
  background: #ffffff;
  border: 1px solid var(--tool-blue);
  box-shadow: 0 1px 4px rgba(24, 160, 251, 0.22);
  animation: ${handleNudge} ${DRAG_MS}ms ${DRAG_EASE} forwards;

  ${({ $corner }) => {
    switch ($corner) {
      case 'tl':
        return 'left: -5px; top: -5px; --x: -1px; --y: -1px;';
      case 'tr':
        return 'right: -5px; top: -5px; --x: 1px; --y: -1px;';
      case 'bl':
        return 'left: -5px; bottom: -5px; --x: -1px; --y: 1px;';
      default:
        return 'right: -5px; bottom: -5px; --x: 1px; --y: 1px;';
    }
  }}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: translate(var(--x), var(--y));
  }
`;

const SizeLabel = styled.div`
  position: absolute;
  left: 18%;
  top: calc(100% + 13px);
  min-width: 78px;
  padding: 4px 8px 5px;
  border-radius: 3px;
  background: var(--tool-blue);
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.02em;
  text-align: center;
  box-shadow: 0 8px 18px rgba(24, 160, 251, 0.18);
  animation: ${labelTrack} ${DRAG_MS}ms ${DRAG_EASE} forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: translate(-50%, 0);
  }
`;

const ResizeCursor = styled.span`
  position: absolute;
  left: 18%;
  top: 100%;
  width: 24px;
  height: 24px;
  color: var(--tool-blue);
  animation: ${cursorDrag} ${DRAG_MS}ms ${DRAG_EASE} forwards;
  transform-origin: 8px 8px;
  filter: drop-shadow(0 5px 9px rgba(24, 160, 251, 0.2));

  &::before {
    content: '';
    position: absolute;
    left: 3px;
    top: 2px;
    width: 0;
    height: 0;
    border-left: 7px solid currentColor;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    transform: rotate(45deg);
  }

  &::after {
    content: '↘';
    position: absolute;
    right: 0;
    bottom: -2px;
    font-family: ${({ theme }) => theme.fonts.mono};
    font-size: 18px;
    font-weight: 800;
    line-height: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Statement = styled(motion.p)`
  position: relative;
  z-index: 2;
  display: block;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: clamp(0.95rem, 4.4vw, 4.2rem);
  font-weight: 400;
  line-height: 1;
  color: ${({ theme }) => theme.colors.ink};
  margin: 0;
  padding: 0;
  white-space: nowrap;
  max-width: 100%;
  transform-origin: center;
  will-change: transform, opacity, clip-path;
  cursor: nwse-resize;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.65);
  animation: ${typeReveal} ${({ $duration }) => $duration}ms
    steps(${({ $chars }) => $chars}, end) forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    clip-path: none;
  }
`;

const ResumeExportLink = styled.a`
  --tool-blue: #18a0fb;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: min(100%, 340px);
  min-height: 76px;
  margin: 24px auto 0;
  padding: 0 42px;
  border-radius: 999px;
  color: #ffffff;
  background: #0c0c11;
  text-decoration: none;
  transform-origin: center;
  animation: ${resumeButtonPulse} 3.2s cubic-bezier(0.18, 0.92, 0.18, 1) infinite;

  &:hover {
    color: #ffffff;
    transform: translateY(-2px) scale(1.018);
  }

  @media (max-width: 768px) {
    width: min(100%, 304px);
    min-height: 68px;
    margin-top: 18px;
    padding: 0 30px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ResumeButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: clamp(1.05rem, 1.8vw, 1.45rem);
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1;
  white-space: nowrap;

  .material-symbols-rounded {
    font-size: 27px;
    color: #ffffff;
  }

  @media (max-width: 768px) {
    gap: 12px;
    font-size: 1.08rem;

    .material-symbols-rounded {
      font-size: 23px;
    }
  }
`;

const BottomBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: flex-end;
  gap: 24px;
  padding: 24px 40px;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 20px max(28px, env(safe-area-inset-bottom));
    gap: 16px;
    text-align: center;
  }
`;

const BarLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 0.7rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
`;

const BarItems = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    width: 100%;
    gap: 14px 24px;
    justify-content: center;
  }

  @media (max-width: 520px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const BarItem = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 0.95rem;
  transition: color 0.25s ease;

  .material-symbols-rounded {
    color: ${({ theme }) => theme.colors.accent};
    font-size: 20px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const BarRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 0.95rem;
  justify-self: end;

  .material-symbols-rounded {
    color: ${({ theme }) => theme.colors.accent};
    font-size: 20px;
  }

  @media (max-width: 900px) {
    justify-self: center;
    justify-content: center;
  }
`;

const FlareIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2v5M12 17v5M4.93 4.93l3.54 3.54M15.54 15.54l3.54 3.54M2 12h5M17 12h5M4.93 19.07l3.54-3.54M15.54 8.46l3.54-3.54"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const STATEMENTS = [
  'I am a full-stack developer',
  'I ship full-stack apps end-to-end',
  'I integrate AI into real products',
  'I build for scale and performance',
  'I have been shipping products since 2021',
  'I am available for hire',
];

// Readout climbs as the box widens, then freezes on the final size.
const SIZE_READOUT = ['198 × 88', '286 × 122', '378 × 148', '452 × 168', '526 × 184'];

const Hero = () => {

  const [index, setIndex] = useState(0);
  const [sizeIndex, setSizeIndex] = useState(0);
  const [dragDone, setDragDone] = useState(false);

  // Tick the readout alongside the one-shot sweep, then settle.
  useEffect(() => {
    const tick = setInterval(() => {
      setSizeIndex((i) => Math.min(i + 1, SIZE_READOUT.length - 1));
    }, DRAG_MS / SIZE_READOUT.length);

    const settle = setTimeout(() => {
      setSizeIndex(SIZE_READOUT.length - 1);
      setDragDone(true);
    }, DRAG_MS);

    return () => {
      clearInterval(tick);
      clearTimeout(settle);
    };
  }, []);

  // Statements only start cycling once the box has finished being dragged out.
  useEffect(() => {
    if (!dragDone) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % STATEMENTS.length);
    }, TYPE_MS + HOLD_MS);
    return () => clearInterval(id);
  }, [dragDone]);

  return (
    <HeroSection id="home">
      <Welcome
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        Welcome! I'm Jabez
        <Flare>
          <FlareIcon />
        </Flare>
      </Welcome>

      <StatementWrap>
        <StatementStage>
          <ToolPlane aria-hidden="true">
            <ResizeOverlay>
              <Handle $corner="tl" />
              <Handle $corner="tr" />
              <Handle $corner="bl" />
              <Handle $corner="br" />
            </ResizeOverlay>
            <ResizeCursor />
            <SizeLabel>{SIZE_READOUT[sizeIndex]}</SizeLabel>
          </ToolPlane>
          <AnimatePresence mode="wait">
            <Statement
              key={index}
              $chars={STATEMENTS[index].length}
              $duration={dragDone ? TYPE_MS : DRAG_MS}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              whileHover={{ scale: 1.035 }}
              whileTap={{ scale: 0.985 }}
              transition={{
                duration: 0.42,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {STATEMENTS[index]}
            </Statement>
          </AnimatePresence>
        </StatementStage>
      </StatementWrap>

      <ResumeExportLink href="/resume.pdf" download="Jabez_Llave_Resume.pdf" aria-label="Download resume">
        <ResumeButtonContent>
          <span className="material-symbols-rounded">picture_as_pdf</span>
          Download resume
        </ResumeButtonContent>
      </ResumeExportLink>

      <BottomBar>
        <BarLabel>Get in Touch</BarLabel>
        <BarItems>
          <BarItem href="mailto:jabezllave@gmail.com">
            <span className="material-symbols-rounded">id_card</span>
            jabezllave@gmail.com
          </BarItem>
          <BarItem
            href="https://github.com/Jabex113"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="material-symbols-rounded">hub</span>
            GitHub
          </BarItem>
        </BarItems>
        <BarRight>
          <span className="material-symbols-rounded">home_pin</span>
          Caloocan, Philippines
        </BarRight>
      </BottomBar>
    </HeroSection>
  );
};

export default Hero;
